import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Product } from "@/entities/Product";
import { SavedItem } from "@/entities/SavedItem";
import { Button } from "@/components/ui/button.jsx";
import { Heart, ChevronDown, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import ProductRecommendations from "../components/products/ProductRecommendations";
import { addToCart } from "@/utils/cart";

export default function ProductDetailPage() {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [shippingOpen, setShippingOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fixed: Added success message state for better UX
  const [successMessage, setSuccessMessage] = useState('');
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    if (productId) {
      loadProduct();
      checkIfSaved();
    } else {
      // Fixed: Handle missing product ID
      setError('Product not found');
      setIsLoading(false);
    }
  }, [productId]);

  const loadProduct = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await Product.getById(productId);
      if (data) {
        setProduct(data);
        // Fixed: Initialize with first available size
        setSelectedSize(data.sizes?.[0] || null);
      } else {
        setError('Product not found');
      }
    } catch (err) {
      console.error('Error loading product:', err);
      setError('Failed to load product');
    } finally {
      setIsLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const saved = await SavedItem.filter({ product_id: productId });
      setIsSaved(saved.length > 0);
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const toggleSave = async () => {
    try {
      if (isSaved) {
        const saved = await SavedItem.filter({ product_id: productId });
        if (saved.length > 0) {
          await SavedItem.delete(saved[0].id);
        }
        setSuccessMessage('Removed from favorites');
      } else {
        await SavedItem.create({ product_id: productId });
        setSuccessMessage('Added to favorites');
      }
      setIsSaved(!isSaved);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling save:', error);
      setError('Failed to update favorites');
    }
  };

  const addToBag = () => {
    // Fixed: Validate size selection for products that have sizes
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      setError('Please select a size');
      return;
    }

    // Fixed: Improved cart item structure with validation
    const cartItem = {
      productId: product.id,
      name: product.name || 'Unnamed Product',
      designer: product.designer || '',
      price: product.price || 0,
      size: selectedSize,
      quantity: 1,
      image: product.images?.[0] || ''
    };
    
    try {
      addToCart(cartItem);

      setSuccessMessage('Added to bag!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setError('Failed to add to bag');
    }
  };

  // Fixed: Better error handling UI
  if (error && !product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="panel p-8 text-center">
          <AlertCircle className="w-16 h-16 text-[var(--muted)] mx-auto mb-4" />
          <p className="text-[var(--muted)] mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/')}
            className="btn-outline px-5 py-2.5"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--brand)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen pb-10">
      {/* Fixed: Better success/error message display */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50"
          >
            <Alert className="bg-[var(--brand)] text-white border-0">
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          </motion.div>
        )}
        {error && product && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50"
          >
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-wrap panel overflow-hidden grid lg:grid-cols-2">
        {/* Image Gallery */}
        <div className="relative" style={{ background: product.bgColor || 'var(--accent-soft)' }}>
          <motion.div 
            key={selectedImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-[60vh] lg:h-full lg:min-h-[760px] sticky top-0"
            style={{ background: product.bgColor || 'var(--accent-soft)' }}
          >
            <img 
              src={product.images?.[selectedImage] || 'https://images.unsplash.com/photo-1643902854732-f398c85e3b0d?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1170'}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </motion.div>

          {/* Thumbnail Navigation - Fixed: Only show if multiple images exist */}
          {product.images && product.images.length > 1 && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3">
              {product.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === index ? 'border-[var(--brand)]' : 'border-transparent opacity-50'
                  }`}
                  aria-label={`View image ${index + 1}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-8 md:p-10 lg:p-12 bg-[var(--paper)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {product.designer && (
              <p className="chip mb-4">by {product.designer}</p>
            )}
            <h1 className="text-4xl md:text-5xl mb-5">{product.name}</h1>
            
            {product.description && (
              <p className="text-[var(--muted)] mb-6 leading-relaxed">
                {product.description}
              </p>
            )}

            <p className="text-2xl mb-8">${product.price?.toFixed(2)}</p>

            {product.maxInventory && product.inventory > 0 && (
              <div className="mb-6 pb-6 border-b border-[var(--line)]">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-[var(--line)] relative overflow-hidden rounded-full">
                    <div
                      className="absolute inset-y-0 left-0 bg-[var(--brand)] rounded-full transition-all"
                      style={{ width: `${(product.inventory / product.maxInventory) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs font-['Space_Mono'] text-[var(--muted)] shrink-0">
                    {product.inventory} <span className="text-[var(--ink)]">left</span> of {product.maxInventory}
                  </p>
                </div>
              </div>
            )}

            {product.limited_edition && (
              <div className="mb-8 pb-8 border-b border-[var(--line)]">
                <p className="text-sm text-[var(--muted)]">
                  This collection is limited to{' '}
                  <span className="text-[var(--ink)] font-medium">{product.limited_edition_count}</span>{' '}
                  pieces.
                </p>
              </div>
            )}

            {/* Color */}
            {product.color && (
              <div className="mb-6 pb-6 border-b border-[var(--line)]">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--muted)]">COLOR</span>
                  <span className="text-sm uppercase">{product.color}</span>
                </div>
              </div>
            )}

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mb-8">
                <p className="text-sm text-[var(--muted)] mb-4">SELECT SIZE</p>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => {
                    const sizeQty = product.sizeInventory ? product.sizeInventory[size] : null;
                    const isOos = sizeQty === 0;
                    return (
                      <button
                        key={size}
                        onClick={() => {
                          if (!isOos) { setSelectedSize(size); setError(null); }
                        }}
                        className={`relative px-6 py-3 border rounded-full transition-all ${
                          isOos
                            ? 'border-[var(--line)] text-[var(--muted)] cursor-not-allowed'
                            : selectedSize === size
                            ? 'border-[var(--brand)] bg-[var(--brand)] text-white'
                            : 'border-[var(--line)] text-[var(--ink)] hover:border-[var(--brand)]'
                        }`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-4 mb-8">
              {product.inventory === 0 ? (
                <Button
                  disabled
                  className="w-full h-14 text-base opacity-50 cursor-not-allowed bg-[var(--paper)] text-[var(--muted)] border border-[var(--line)]"
                >
                  OUT OF STOCK
                </Button>
              ) : (
                <Button 
                  onClick={addToBag}
                  className="w-full btn-brand h-14 text-base"
                >
                  ADD TO BAG
                </Button>
              )}
              <Button 
                onClick={toggleSave}
                variant="outline"
                className="w-full btn-outline h-14 text-base"
              >
                <Heart className={`w-5 h-5 mr-2 ${isSaved ? 'fill-current' : ''}`} />
                {isSaved ? 'SAVED' : 'ADD TO FAVORITES'}
              </Button>
            </div>

            {/* Accordions */}
            <div className="space-y-1 border-t border-[var(--line)]">
              <button
                onClick={() => setDetailsOpen(!detailsOpen)}
                className="w-full py-6 flex justify-between items-center border-b border-[var(--line)]"
              >
                <span className="text-sm tracking-wider">DETAILS</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {detailsOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                      <div className="py-4 text-sm text-[var(--muted)]">
                        {product.category === 'apparel' && product.name === 'Designer T-Shirt' && (
                          <>
                            <p>
                              Premium 100% cotton black t-shirt, designed for comfort, durability, and a clean modern look. The soft, breathable cotton fabric delivers all-day comfort, while the classic fit makes it perfect for any wardrobe.
                            </p>
                            <ul className="list-disc ml-6 mt-4">
                              <li>Material: 100% soft, breathable cotton</li>
                              <li>Color: Black</li>
                              <li>Fit: Classic unisex fit with reinforced stitching</li>
                              <li>Care: Machine washable</li>
                            </ul>
                          </>
                        )}
                        {product.category === 'apparel' && product.name === 'Black Designer Crew Neck Sweater' && (
                          <>
                            <p>
                              Premium black crew neck sweater with a clean, structured silhouette designed for everyday wear. The midweight blend delivers comfort and durability, making it a versatile staple for any wardrobe.
                            </p>
                            <ul className="list-disc ml-6 mt-4 space-y-1">
                              <li>Material: 50% Cotton / 50% Polyester</li>
                              <li>Color: Black</li>
                              <li>Fit: Classic unisex fit</li>
                              <li>Care: Machine wash cold on gentle cycle; wash with like colors; do not bleach; tumble dry low</li>
                              <li>Origin: Made in Honduras</li>
                            </ul>
                          </>
                        )}
                        {product.category === 'apparel' && product.name === 'Premium Black Hoodie' && (
                          <>
                            <p>
                              Premium black hoodie designed for comfort, durability, and everyday style. Crafted with a soft, cozy interior and sturdy construction, it delivers reliable warmth while keeping a clean, modern look. The standout detail is the reflective 3M embroidery, adding a sleek, high-visibility accent that catches light and elevates your overall fit.
                            </p>
                            <ul className="list-disc ml-6 mt-4">
                              <li>Material: 65% Cotton / 25% Recycled Polyester / 10% Polyester</li>
                              <li>Color: Black</li>
                              <li>Fit: Relaxed unisex fit</li>
                              <li>Care: Machine wash cold on gentle cycle; wash with like colors; do not bleach; tumble dry low; do not iron decoration</li>
                              <li>Origin: Made in Pakistan</li>
                            </ul>
                          </>
                        )}
                        {product.category === 'apparel' && product.name === 'Tan Hoodie' && (
                          <>
                            <p>
                              Premium tan hoodie designed for comfort, durability, and everyday style. Crafted with a soft, cozy interior and sturdy construction, it delivers reliable warmth while keeping a clean, modern look. The standout detail is the reflective 3M embroidery, adding a sleek, high-visibility accent that catches light and elevates your overall fit.
                            </p>
                            <ul className="list-disc ml-6 mt-4">
                              <li>Material: 65% Cotton / 25% Recycled Polyester / 10% Polyester</li>
                              <li>Color: Tan</li>
                              <li>Fit: Classic unisex fit</li>
                              <li>Care: Machine wash cold on gentle cycle; wash with like colors; do not bleach; tumble dry low; do not iron decoration</li>
                              <li>Origin: Made in Pakistan</li>
                            </ul>
                          </>
                        )}
                        {product.category === 'apparel' && product.name === 'Grey Hoodie' && (
                          <>
                            <p>
                              Premium grey hoodie designed for comfort, durability, and everyday style. Crafted with a soft, cozy interior and sturdy construction, it delivers reliable warmth while keeping a clean, modern look. The standout detail is the reflective 3M embroidery, adding a sleek, high-visibility accent that catches light and elevates your overall fit.
                            </p>
                            <ul className="list-disc ml-6 mt-4">
                              <li>Material: 60% Cotton / 30% Recycled Polyester / 10% Polyester</li>
                              <li>Color: Grey</li>
                              <li>Fit: Relaxed unisex fit</li>
                              <li>Care: Machine wash cold on gentle cycle; wash with like colors; do not bleach; tumble dry low; do not iron decoration</li>
                              <li>Origin: Made in Pakistan</li>
                            </ul>
                          </>
                        )}
                        {/* 3D Products */}
                        {product.category === '3d_products' && (
                          <>
                            <p>
                              Each piece is 3D printed using durable PETG filament, with body work and paint applied entirely by hand — making every model a one-of-a-kind collectible.
                            </p>
                            <ul className="list-disc ml-6 mt-4 space-y-1">
                              <li>Material: PETG filament</li>
                              <li>Length: ~10 in</li>
                              <li>Height: ~2¼ in</li>
                              <li>Width: ~3¾ in</li>
                              <li>3D printed body</li>
                              <li>Body work finished by hand</li>
                              <li>Painted by hand</li>
                            </ul>
                          </>
                        )}
                        {/* Fallback for any other non-apparel, non-3D products */}
                        {product.category !== 'apparel' && product.category !== '3d_products' && (
                          <>
                            <p>{product.description || 'No additional details available.'}</p>
                          </>
                        )}
                      </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={() => setShippingOpen(!shippingOpen)}
                className="w-full py-6 flex justify-between items-center border-b border-[var(--line)]"
              >
                <span className="text-sm tracking-wider">SHIPPING</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${shippingOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {shippingOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="py-4 text-sm text-[var(--muted)] space-y-2">
                      <p>Standard shipping cost included on all orders.</p>
                      <p>Ships via USPS Ground — estimated delivery in 2–5 business days.</p>
                      <p>We currently ship to US addresses only.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Recommendations */}
      <ProductRecommendations currentProductId={productId} category={product.category} />

      {/* Full Image Modal */}
      <AnimatePresence>
        {showFullImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setShowFullImage(false)}
          >
            <button
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors"
              onClick={() => setShowFullImage(false)}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={product.images?.[selectedImage]}
              alt={product.name}
              className="max-h-[95vh] max-w-[95vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}